import type { Plan } from '@platform/types';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, MessageSquare, Phone, Rocket } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { GuaranteeBadge } from '@/components/marketing/guarantee-badge';
import { AddOnsSection } from '@/components/marketing/add-ons-section';
import { SETUP_CALL_URL } from '@/components/marketing/done-for-you-section';
import { PlanComparison } from '@/components/marketing/plan-comparison';
import { SavingsCalculator } from '@/components/marketing/savings-calculator';
import { PlanCard } from '@/components/plan-card';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';
import { iosUrl, androidUrl } from '@/lib/app-links';
import { apiFetch } from '@/lib/api-client';
import { detectDisplayCurrency, isDisplayCurrency } from '@/lib/currency';
import { Seo } from '@/lib/seo';
import { useOrganization } from '@/lib/use-organization';
import { useAuthStore } from '@/store/auth-store';

/**
 * Public pricing page. Reads `GET /v1/plans` (only active plans come back —
 * parked tiers are hidden server-side) and renders one card per plan, plus a
 * Custom card that routes to the /contact form for anything beyond the
 * self-serve tiers. Clicking a plan sends the user to `/signup` (anonymous)
 * or `/app/billing/upgrade?plan=<slug>` (already signed in).
 */
export function PricingPage(): JSX.Element {
  const brand = useBrand();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  // Signed-in users see prices converted to their org's display currency;
  // anonymous visitors get a browser-locale guess. Conversion is display-only
  // — checkout always charges the plan's own (USD) price.
  const { data: org } = useOrganization();
  const displayCurrency =
    org && isDisplayCurrency(org.displayCurrency)
      ? org.displayCurrency
      : detectDisplayCurrency();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans', displayCurrency],
    queryFn: () =>
      apiFetch<Plan[]>(`/plans?displayCurrency=${encodeURIComponent(displayCurrency)}`),
    // Pricing rarely changes — let it cache across sessions.
    staleTime: 60_000,
  });

  const handleChoose = (plan: Plan): void => {
    track('pricing_cta_click', { plan: plan.slug, authed: Boolean(user) });
    if (plan.isTrial) {
      navigate(user ? '/app' : '/signup');
      return;
    }
    if (user) {
      navigate(`/app/billing/upgrade?plan=${plan.slug}`);
    } else {
      navigate(`/signup?plan=${plan.slug}`);
    }
  };

  return (
    <>
    <section className="container py-16">
      <Seo
        title={`Pricing — ${brand.displayName}`}
        description="Simple, predictable pricing for AI voice and chat. Every plan bundles both channels on one knowledge base, from $39/month. Talk to us about a custom plan."
        path="/pricing"
      />
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight">Simple, honest pricing</h1>
        <p className="mt-4 text-muted-foreground">
          Every plan bundles voice and chat on one knowledge base — from $39/month.
          Each tier includes inbound voice minutes and chat messages, always-on
          24/7/365. Need more volume or a custom setup? Talk to us and we&apos;ll
          build a plan around your business.
        </p>
      </div>

      {isLoading || !plans ? (
        // Reserve the cards' height so async load doesn't shift layout (CLS).
        <div className="mx-auto mt-12 grid min-h-[460px] max-w-3xl gap-6 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-[440px] animate-pulse rounded-2xl border border-border bg-muted/30"
            />
          ))}
        </div>
      ) : (
        <div className="mx-auto mt-12 grid max-w-3xl gap-6 sm:grid-cols-2">
          <h2 className="sr-only">Plans</h2>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              name={plan.name}
              description={plan.description}
              priceCents={plan.priceCents}
              currency={plan.currency}
              displayPrice={plan.displayPrice}
              voiceMinutesIncluded={plan.voiceMinutesIncluded}
              chatMessagesIncluded={plan.chatMessagesIncluded}
              concurrency={20}
              callTransferEnabled={plan.callTransferEnabled}
              isTrial={plan.isTrial}
              trialDays={plan.trialDays}
              channels={['voice', 'chat']}
              highlighted
              badge="Voice + chat included"
              ctaLabel="Get started"
              onCta={() => handleChoose(plan)}
              footer={
                !plan.isTrial && plan.overagePerMinuteCents ? (
                  <p className="text-xs text-muted-foreground">
                    Extra usage (opt-in): ${(plan.overagePerMinuteCents / 100).toFixed(2)}/min
                    {plan.overagePer1000MessagesCents
                      ? ` · $${(plan.overagePer1000MessagesCents / 100).toFixed(0)} per 1,000 messages`
                      : ''}
                  </p>
                ) : undefined
              }
            />
          ))}
          <CustomPlanCard />
        </div>
      )}

      <GuaranteeBadge variant="banner" className="mt-8" />

      {displayCurrency !== 'usd' ? (
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Converted prices are approximate, refreshed daily from ECB rates. All plans
          are billed in USD — your card issuer applies its own exchange rate.
        </p>
      ) : null}

      <p className="mt-4 text-center text-xs text-muted-foreground">
        All plans include the free iPhone &amp; Android app{iosUrl && androidUrl ? '' : ' (coming soon)'} —{' '}
        <a href="/#mobile-app" className="font-medium text-[var(--brand-accent)] hover:underline">
          learn more
        </a>
        .
      </p>

      <DfyBanner />

      {plans ? <PlanComparison plans={plans} /> : null}
    </section>
    {/* Relocated from the landing page — add-ons are a pricing decision. */}
    <AddOnsSection />
    <SavingsCalculator />
    </>
  );
}

/**
 * Done-for-you upsell strip. The prices above are self-serve; this surfaces the
 * "we set it up for you" path (Setup Concierge, $99 one-time) for the
 * non-technical majority. Routes to the contact intake with a DFY prefill.
 * Keep the $99 / 72h copy in sync with docs/GTM-DONE-FOR-YOU-PLAN.md.
 */
function DfyBanner(): JSX.Element {
  return (
    <div className="mx-auto mt-14 max-w-4xl rounded-2xl border border-primary/30 bg-primary/5 p-8 sm:flex sm:items-center sm:justify-between sm:gap-8">
      <div className="sm:max-w-xl">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" aria-hidden="true" />
          <h2 className="text-xl font-semibold">Don&apos;t want to set it up yourself?</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Our team builds your knowledge base, sets up call transfers, and takes
          it live in 72 hours. You keep your number and approve it on a short
          call. <span className="font-medium text-foreground">$99 one-time</span>{' '}
          plus your monthly plan — setup fee refunded if it&apos;s not answering
          your calls within 72 hours.
        </p>
      </div>
      <div className="mt-6 flex-shrink-0 sm:mt-0">
        <Button asChild size="lg">
          <Link to="/contact?topic=dfy">
            Get it set up for me
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        <p className="mt-2 text-center text-sm">
          or{' '}
          <a href={SETUP_CALL_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--brand-accent)] hover:underline">
            book a 20-min setup call
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * "Custom" tier — no self-serve checkout. Higher call volumes, white-label
 * branding, and bespoke terms all go through the contact form.
 */
function CustomPlanCard(): JSX.Element {
  const features = [
    'Higher monthly call and chat volumes',
    'As many phone numbers as you need',
    'Toll-free, premium & vanity numbers (extra cost)',
    'White-label branding',
    'Custom onboarding and setup',
    'Priority support',
  ];
  return (
    <div className="relative flex flex-col rounded-xl border bg-card p-6 shadow-sm">
      <h3 className="text-lg font-semibold">Custom</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        A plan shaped around your call and chat volume and workflow.
      </p>

      <div className="mt-4">
        <p className="text-3xl font-bold">Let&apos;s talk</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Volume pricing, billed monthly
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            <Phone className="h-3 w-3" />
            Voice
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <MessageSquare className="h-3 w-3" />
            Chat
          </span>
        </div>
      </div>

      <ul className="mt-6 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6">
        <Button asChild variant="outline" className="w-full">
          <Link to="/contact">Contact us</Link>
        </Button>
        <div className="mt-2 text-center text-xs text-muted-foreground">
          We reply within one business day
        </div>
      </div>
    </div>
  );
}
